import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Section from './Section.jsx';
import { timeLabel } from '../lib/format.js';

// "Sun" over "Sep 20" from a YYYY-MM-DD string. Built from a local-noon Date so
// the day never slips across a timezone boundary (new Date("2026-06-20") would
// be UTC midnight, which renders as the prior day in ET). Stacked rather than
// one line: at w-16 "Sun, Sep 20" wrapped as "SUN, SEP" over "20".
function dayParts(dateStr) {
  const [y, m, d] = (dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d, 12);
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
    day: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  };
}

// The next four show; the rest sit behind a disclosure (design review
// 2026-09-26, finding 7). The full list ran 11 rows and 1,023px on a phone,
// eight of them 6 PM or later, in the middle of a page meant to be about today.
const VISIBLE = 4;

// The next 7 days of vetted events (markets, festivals, and Ticketmaster shows).
// Pure data, stamped by the system — the model never touches it. Renders nothing
// when the list is empty, so a quiet week simply hides the section. Where it
// sits is each persona's call (sectionOrder in personas.js, id 'week').
export default function UpcomingWeek({ events }) {
  const [open, setOpen] = useState(false);
  if (!Array.isArray(events) || events.length === 0) return null;
  const hidden = events.length - VISIBLE;
  const shown = open || hidden <= 0 ? events : events.slice(0, VISIBLE);
  return (
    <Section title="This week" eyebrow="The next 7 days">
      {/* Boxed list: cream-100/60 with a navy-100 hairline; white is for inputs only. */}
      <ul
        id="week-list"
        className="divide-y divide-navy-100 overflow-hidden rounded-xl border border-navy-100 bg-cream-100/60"
      >
        {shown.map((ev, i) => {
          const day = dayParts(ev.date);
          return (
            <li key={i} className="flex items-baseline gap-3 px-4 py-3">
              {/* navy-500, not gold: gold small caps are reserved for structural
                  kickers (the Section eyebrow), so a date label stays quiet. */}
              <span className="w-14 shrink-0 text-xs font-semibold uppercase tracking-wide text-navy-500">
                {day && (
                  <>
                    <span className="block">{day.weekday}</span>
                    <span className="block font-medium">{day.day}</span>
                  </>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-navy-900">
                  {ev.name}
                  {ev.time ? (
                    // nowrap: "12 PM" broke as "12" over "PM" in a narrow row.
                    <span className="whitespace-nowrap font-normal text-navy-500">, {timeLabel(ev.time)}</span>
                  ) : null}
                </p>
                {ev.venue ? <p className="truncate text-xs text-navy-500">{ev.venue}</p> : null}
              </div>
              {ev.url ? (
                <a
                  href={ev.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  // Same link treatment as the tonight card, not pink: charlestonpink
                  // is the alert color (stale banner, rain flag), and ten pink links
                  // read as a column of warnings down the list. -my-2 py-2 is
                  // tap padding the negative margins hand back: a 32px target
                  // in a row that does not move.
                  className="-my-2 shrink-0 py-2 text-xs font-medium uppercase tracking-[0.14em] text-navy-800 underline-offset-2 hover:text-navy-900"
                >
                  Tickets <span aria-hidden="true">›</span>
                </a>
              ) : null}
            </li>
          );
        })}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls="week-list"
          className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 text-xs font-medium uppercase tracking-[0.14em] text-navy-800 hover:text-navy-900"
        >
          {open ? 'Show the next four' : `All ${events.length} this week`}
          <ChevronDown
            className={['h-4 w-4 transition-transform', open ? 'rotate-180' : ''].join(' ')}
            aria-hidden="true"
          />
        </button>
      )}
    </Section>
  );
}
